import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { fetchTask } from '@/features/orders/ordering/services/orderWorkflowService.js';
import { fetchJianyingDevices } from '@/features/systemManagement/jianying/services/jianyingDeviceService.js';
import { getJianyingDeviceSchedulingState } from '@/features/systemManagement/jianying/utils/jianyingDevices.js';
import {
  JIANYING_TEMPLATE_RUNTIME_STATUS,
} from '@/features/systemManagement/jianying/constants/jianyingTemplateStatus.js';
import {
  fetchJianyingTemplateAvailability,
} from '@/features/systemManagement/jianying/services/jianyingTemplateService.js';
import {
  canUseProductionAutomation,
} from '@/features/automation/utils/productionAccess.js';
import {
  fetchEligibleJianyingOrders,
  fetchJianyingOrderAutomation,
  processJianyingOrderSmartCrop,
} from '../../../services/jianyingJobService.js';
import {
  cleanupJianyingJobImages,
} from '../../../services/jianyingImageUploadService.js';
import {
  cleanupJianyingJobAudio,
} from '../../../services/jianyingAudioUploadService.js';
import {
  cleanupJianyingJobVideo,
} from '../../../services/jianyingVideoUploadService.js';
import {
  applyJianyingCopyPreset,
  applyJianyingSmartCropResults,
  buildJianyingSlotSections,
  createJianyingRequestGuard,
  createInitialJianyingSlotValues,
  getFirstJianyingFieldErrorKey,
  getFirstJianyingImageFieldErrorKey,
  getJianyingAudioSlots,
  getJianyingDefaultCopyPresetId,
  getJianyingImageSlots,
  getJianyingTextSlots,
  getJianyingVideoSlots,
  normalizeJianyingFieldErrors,
  normalizeJianyingOrderImages,
} from '../../../utils/jianyingSlots.js';

export const getJianyingCreateErrorMessage = (error, fallback) => (
  error.response?.data?.message || error.message || fallback
);

export const getJianyingTemplateRuntimeStatus = (template, availability) => (
  availability[template.id] || JIANYING_TEMPLATE_RUNTIME_STATUS.CHECKING
);

const mergeSmartCropResults = (client, result) => {
  const byId = new Map(
    (Array.isArray(result?.items) ? result.items : [])
      .map((item) => [String(item.image_id), item]),
  );
  return {
    ...client,
    images: (Array.isArray(client?.images) ? client.images : []).map((image) => {
      const processed = byId.get(String(image.id || image.image_id));
      if (!processed) return image;
      return {
        ...image,
        smart_crop_image_url: processed.smart_crop_image_url || null,
        smart_crop_status: processed.status,
        smart_crop_error: processed.error || processed.reprocess_error || null,
      };
    }),
  };
};

const useJianyingCreateWorkspace = ({ orderId, setSearchParams }) => {
  const roleId = Number(sessionStorage.getItem('role_id'));
  const [orders, setOrders] = useState([]);
  const [orderFilter, setOrderFilter] = useState('');
  const [orderPickerExpanded, setOrderPickerExpanded] = useState(!orderId);
  const [templatePickerExpanded, setTemplatePickerExpanded] = useState(false);
  const [devicePickerExpanded, setDevicePickerExpanded] = useState(false);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [ordersMessage, setOrdersMessage] = useState('');
  const [clientInfo, setClientInfo] = useState(null);
  const [automation, setAutomation] = useState(null);
  const [devices, setDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [devicesMessage, setDevicesMessage] = useState('');
  const [templateAvailability, setTemplateAvailability] = useState({});
  const [templateAvailableDeviceIds, setTemplateAvailableDeviceIds] = useState({});
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [selectedCopyPresetId, setSelectedCopyPresetId] = useState('');
  const [preferredDeviceId, setPreferredDeviceId] = useState('');
  const [slotValues, setSlotValues] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [audioFieldErrors, setAudioFieldErrors] = useState({});
  const [imageFieldErrors, setImageFieldErrors] = useState({});
  const [videoFieldErrors, setVideoFieldErrors] = useState({});
  const [activeMaterialType, setActiveMaterialType] = useState('image');
  const [smartCropStatus, setSmartCropStatus] = useState({ state: 'idle' });
  const [loading, setLoading] = useState(Boolean(orderId));
  const [message, setMessage] = useState('');
  const fieldRefs = useRef(new Map());
  const imageFieldRefs = useRef(new Map());
  const orderRequestGuard = useRef(null);
  const availabilityRequestId = useRef(0);
  const uploadedAudioKeys = useRef(new Set());
  const uploadedVideoKeys = useRef(new Set());
  const uploadedImageKeys = useRef(new Set());
  if (!orderRequestGuard.current) {
    orderRequestGuard.current = createJianyingRequestGuard();
  }

  const canUseJianying = canUseProductionAutomation(roleId);

  const cleanupAudioKeys = useCallback((keys) => {
    const objectKeys = [...new Set(keys || [])].filter(Boolean);
    if (!orderId || objectKeys.length === 0) return;
    cleanupJianyingJobAudio({ clientId: orderId, objectKeys })
      .then(() => {
        objectKeys.forEach((key) => uploadedAudioKeys.current.delete(key));
      })
      .catch(() => {});
  }, [orderId]);

  const cleanupVideoKeys = useCallback((keys) => {
    const objectKeys = [...new Set(keys || [])].filter(Boolean);
    if (!orderId || objectKeys.length === 0) return;
    cleanupJianyingJobVideo({ clientId: orderId, objectKeys })
      .then(() => {
        objectKeys.forEach((key) => uploadedVideoKeys.current.delete(key));
      })
      .catch(() => {});
  }, [orderId]);

  const cleanupImageKeys = useCallback((keys) => {
    const objectKeys = [...new Set(keys || [])].filter(Boolean);
    if (!orderId || objectKeys.length === 0) return;
    cleanupJianyingJobImages({ clientId: orderId, objectKeys })
      .then(() => {
        objectKeys.forEach((key) => uploadedImageKeys.current.delete(key));
      })
      .catch(() => {});
  }, [orderId]);

  useEffect(() => () => {
    cleanupAudioKeys([...uploadedAudioKeys.current]);
    cleanupVideoKeys([...uploadedVideoKeys.current]);
    cleanupImageKeys([...uploadedImageKeys.current]);
  }, [cleanupAudioKeys, cleanupImageKeys, cleanupVideoKeys]);

  useEffect(() => {
    if (!canUseJianying) {
      setOrdersLoading(false);
      return undefined;
    }
    let active = true;
    setOrdersLoading(true);
    fetchEligibleJianyingOrders()
      .then((data) => {
        if (active) setOrders(data);
      })
      .catch((error) => {
        if (active) setOrdersMessage(getJianyingCreateErrorMessage(error, '读取可制作订单失败'));
      })
      .finally(() => {
        if (active) setOrdersLoading(false);
      });
    return () => {
      active = false;
    };
  }, [canUseJianying]);

  const loadOrder = useCallback(async () => {
    const requestId = orderRequestGuard.current.begin();
    if (!orderId || !canUseJianying) {
      setClientInfo(null);
      setAutomation(null);
      setTemplateAvailability({});
      setTemplateAvailableDeviceIds({});
      setSelectedTemplateId('');
      setPreferredDeviceId('');
      setSlotValues({});
      setFieldErrors({});
      setAudioFieldErrors({});
      setImageFieldErrors({});
      setVideoFieldErrors({});
      setSmartCropStatus({ state: 'idle' });
      fieldRefs.current.clear();
      imageFieldRefs.current.clear();
      setOrderPickerExpanded(true);
      setTemplatePickerExpanded(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    availabilityRequestId.current += 1;
    setTemplateAvailability({});
    setTemplateAvailableDeviceIds({});
    setSelectedTemplateId('');
    setPreferredDeviceId('');
    setSlotValues({});
    setMessage('');
    setFieldErrors({});
    setAudioFieldErrors({});
    setImageFieldErrors({});
    setVideoFieldErrors({});
    setSmartCropStatus({ state: 'idle' });
    fieldRefs.current.clear();
    imageFieldRefs.current.clear();
    try {
      const [orderData, automationData] = await Promise.all([
        fetchTask(orderId),
        fetchJianyingOrderAutomation(orderId),
      ]);
      if (!orderRequestGuard.current.isCurrent(requestId)) return;
      setClientInfo(orderData);
      setAutomation(automationData);
      setSelectedTemplateId('');
      setSlotValues({});
      setOrderPickerExpanded(false);
      setTemplatePickerExpanded(true);
    } catch (error) {
      if (!orderRequestGuard.current.isCurrent(requestId)) return;
      setClientInfo(null);
      setAutomation(null);
      setTemplateAvailability({});
      setTemplateAvailableDeviceIds({});
      setSelectedTemplateId('');
      setPreferredDeviceId('');
      setSlotValues({});
      setOrderPickerExpanded(true);
      setTemplatePickerExpanded(false);
      setMessage(getJianyingCreateErrorMessage(error, '读取订单的剪映配置失败'));
    } finally {
      if (orderRequestGuard.current.isCurrent(requestId)) {
        setLoading(false);
      }
    }
  }, [canUseJianying, orderId]);

  useEffect(() => {
    loadOrder();
    return () => orderRequestGuard.current.invalidate();
  }, [loadOrder]);

  const eligibleOrders = useMemo(() => {
    const keyword = orderFilter.trim().toLowerCase();
    if (!keyword) return orders;
    return orders.filter((order) => [
      order.client_id,
      order.client_name,
      order.video_type,
    ].some((value) => String(value || '').toLowerCase().includes(keyword)));
  }, [orderFilter, orders]);

  const selectedOrder = useMemo(
    () => orders.find((order) => String(order.client_id) === String(orderId)) || null,
    [orderId, orders],
  );
  const clientName = clientInfo?.clientName
    || clientInfo?.client_name
    || automation?.client?.clientName
    || selectedOrder?.client_name
    || orderId;

  const chooseOrder = (clientId) => {
    orderRequestGuard.current.invalidate();
    setClientInfo(null);
    setAutomation(null);
    availabilityRequestId.current += 1;
    setTemplateAvailability({});
    setTemplateAvailableDeviceIds({});
    setSelectedTemplateId('');
    setPreferredDeviceId('');
    setSlotValues({});
    setFieldErrors({});
    setAudioFieldErrors({});
    setImageFieldErrors({});
    setVideoFieldErrors({});
    setSmartCropStatus({ state: 'idle' });
    cleanupAudioKeys([...uploadedAudioKeys.current]);
    cleanupVideoKeys([...uploadedVideoKeys.current]);
    cleanupImageKeys([...uploadedImageKeys.current]);
    fieldRefs.current.clear();
    imageFieldRefs.current.clear();
    setLoading(true);
    setMessage('');
    setOrderPickerExpanded(false);
    setTemplatePickerExpanded(true);
    setSearchParams({ client_id: String(clientId) });
  };

  const templates = useMemo(() => automation?.templates || [], [automation]);
  const orderImages = useMemo(
    () => normalizeJianyingOrderImages(clientInfo?.images || []),
    [clientInfo?.images],
  );
  const orderImageIdentity = useMemo(
    () => orderImages.map((image) => String(image.id)).join('|'),
    [orderImages],
  );
  useEffect(() => {
    const requestId = availabilityRequestId.current + 1;
    availabilityRequestId.current = requestId;
    const templateIds = templates.map((template) => template.id);
    if (!orderId || templateIds.length === 0) {
      setTemplateAvailability({});
      setTemplateAvailableDeviceIds({});
      setPreferredDeviceId('');
      return undefined;
    }

    setTemplateAvailability(Object.fromEntries(
      templateIds.map((id) => [
        id,
        JIANYING_TEMPLATE_RUNTIME_STATUS.CHECKING,
      ]),
    ));
    setTemplateAvailableDeviceIds({});
    setSelectedTemplateId('');
    setPreferredDeviceId('');
    setSlotValues({});

    fetchJianyingTemplateAvailability(templateIds)
      .then((items) => {
        if (availabilityRequestId.current !== requestId) return;
        const responseStatuses = Object.fromEntries(
          items.map((item) => [item.template_id, item.status]),
        );
        setTemplateAvailableDeviceIds(Object.fromEntries(
          items.map((item) => [
            item.template_id,
            Array.isArray(item.available_device_ids)
              ? item.available_device_ids
              : [],
          ]),
        ));
        const nextAvailability = Object.fromEntries(
          templateIds.map((id) => [
            id,
            responseStatuses[id] || JIANYING_TEMPLATE_RUNTIME_STATUS.UNREADABLE,
          ]),
        );
        setTemplateAvailability(nextAvailability);

        const onlyTemplate = templates.length === 1
          && nextAvailability[templates[0].id]
            === JIANYING_TEMPLATE_RUNTIME_STATUS.AVAILABLE
          ? templates[0]
          : null;
        if (onlyTemplate) {
          setSelectedTemplateId(onlyTemplate.id);
          setSelectedCopyPresetId(getJianyingDefaultCopyPresetId(onlyTemplate));
          setSlotValues(createInitialJianyingSlotValues(
            onlyTemplate,
            clientName,
            orderImages,
          ));
          setTemplatePickerExpanded(false);
        }
      })
      .catch(() => {
        if (availabilityRequestId.current !== requestId) return;
        setTemplateAvailableDeviceIds({});
        setPreferredDeviceId('');
        setTemplateAvailability(Object.fromEntries(
          templateIds.map((id) => [
            id,
            JIANYING_TEMPLATE_RUNTIME_STATUS.SERVICE_OFFLINE,
          ]),
        ));
      });

    return () => {
      if (availabilityRequestId.current === requestId) {
        availabilityRequestId.current += 1;
      }
    };
  // Smart-crop updates change orderImages but must not reset current slot selections.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientName, orderId, orderImageIdentity, templates]);

  const selectedTemplate = useMemo(
    () => templates.find((template) => String(template.id) === String(selectedTemplateId)) || null,
    [selectedTemplateId, templates],
  );
  const preferredDevices = useMemo(() => {
    const availableIds = new Set(
      (templateAvailableDeviceIds[selectedTemplateId] || []).map(String),
    );
    return devices
      .filter(
        (device) =>
          getJianyingDeviceSchedulingState(device).schedulable
          && availableIds.has(String(device.id)),
      )
      .sort(
        (left, right) => Number(left.priority || 0) - Number(right.priority || 0)
          || String(left.name || '').localeCompare(String(right.name || ''), 'zh-CN'),
      );
  }, [devices, selectedTemplateId, templateAvailableDeviceIds]);
  const openDevicePicker = useCallback(() => {
    if (!selectedTemplate) return;
    setDevicePickerExpanded(true);
    setDevicesLoading(true);
    setDevicesMessage('');
    fetchJianyingDevices()
      .then((data) => setDevices(data))
      .catch((error) => {
        setDevices([]);
        setDevicesMessage(getJianyingCreateErrorMessage(
          error,
          '设备列表读取失败，仍可由系统自动选择。',
        ));
      })
      .finally(() => setDevicesLoading(false));
  }, [selectedTemplate]);
  useEffect(() => {
    if (
      preferredDeviceId
      && !preferredDevices.some(
        (device) => String(device.id) === String(preferredDeviceId),
      )
    ) {
      setPreferredDeviceId('');
    }
  }, [preferredDeviceId, preferredDevices]);
  const textSections = useMemo(
    () => buildJianyingSlotSections(selectedTemplate, 'text'),
    [selectedTemplate],
  );
  const audioSections = useMemo(
    () => buildJianyingSlotSections(selectedTemplate, 'audio'),
    [selectedTemplate],
  );
  const videoSections = useMemo(
    () => buildJianyingSlotSections(selectedTemplate, 'video'),
    [selectedTemplate],
  );
  const audioSlots = useMemo(
    () => getJianyingAudioSlots(selectedTemplate),
    [selectedTemplate],
  );
  const videoSlots = useMemo(
    () => getJianyingVideoSlots(selectedTemplate),
    [selectedTemplate],
  );
  const textSlots = useMemo(
    () => getJianyingTextSlots(selectedTemplate),
    [selectedTemplate],
  );
  const imageSlots = useMemo(
    () => getJianyingImageSlots(selectedTemplate),
    [selectedTemplate],
  );
  const imageSections = useMemo(
    () => buildJianyingSlotSections(selectedTemplate, 'image'),
    [selectedTemplate],
  );
  const materialTypes = useMemo(() => [
    { key: 'image', icon: 'bi-images', label: '图片', count: imageSlots.length },
    { key: 'text', icon: 'bi-fonts', label: '文字', count: textSlots.length },
    { key: 'audio', icon: 'bi-music-note-beamed', label: '音频', count: audioSlots.length },
    { key: 'video', icon: 'bi-camera-video', label: '视频', count: videoSlots.length },
  ].filter((item) => item.count > 0), [
    audioSlots.length,
    imageSlots.length,
    textSlots.length,
    videoSlots.length,
  ]);
  useEffect(() => {
    if (
      materialTypes.length
      && !materialTypes.some((item) => item.key === activeMaterialType)
    ) {
      setActiveMaterialType(materialTypes[0].key);
    }
  }, [activeMaterialType, materialTypes]);
  const applySmartCropResult = useCallback((result) => {
    setClientInfo((current) => mergeSmartCropResults(current, result));
    setSlotValues((current) => applyJianyingSmartCropResults(current, result?.items));
  }, []);

  const processAllSmartCrop = useCallback(async () => {
    if (!orderId || smartCropStatus.state === 'processing') return;
    setSmartCropStatus({ state: 'processing' });
    try {
      const result = await processJianyingOrderSmartCrop({
        clientId: orderId,
        force: true,
      });
      applySmartCropResult(result);
      setSmartCropStatus({ state: 'complete', summary: result?.summary || {} });
    } catch (error) {
      setSmartCropStatus({
        state: 'error',
        message: getJianyingCreateErrorMessage(error, '自动裁剪失败，已保留现有图片'),
      });
    }
  }, [applySmartCropResult, orderId, smartCropStatus.state]);

  const processSingleSmartCrop = useCallback(async (image) => {
    const imageId = image?.id || image?.imageId;
    if (!orderId || !imageId || smartCropStatus.state === 'processing') return;
    setClientInfo((current) => ({
      ...current,
      images: (Array.isArray(current?.images) ? current.images : []).map((item) => (
        String(item.id || item.image_id) === String(imageId)
          ? { ...item, smart_crop_status: 'processing', smart_crop_error: null }
          : item
      )),
    }));
    try {
      const result = await processJianyingOrderSmartCrop({
        clientId: orderId,
        imageIds: [imageId],
        force: true,
      });
      applySmartCropResult(result);
    } catch (error) {
      const errorMessage = getJianyingCreateErrorMessage(error, '自动裁剪失败，已保留原图');
      setClientInfo((current) => ({
        ...current,
        images: (Array.isArray(current?.images) ? current.images : []).map((item) => (
          String(item.id || item.image_id) === String(imageId)
            ? {
              ...item,
              smart_crop_status: item.smart_crop_image_url ? 'succeeded' : 'failed',
              smart_crop_error: errorMessage,
            }
            : item
        )),
      }));
      setMessage(errorMessage);
    }
  }, [applySmartCropResult, orderId, smartCropStatus.state]);
  const chooseTemplate = (template) => {
    const runtimeStatus = getJianyingTemplateRuntimeStatus(template, templateAvailability);
    if (runtimeStatus !== JIANYING_TEMPLATE_RUNTIME_STATUS.AVAILABLE) return;
    cleanupAudioKeys([...uploadedAudioKeys.current]);
    cleanupVideoKeys([...uploadedVideoKeys.current]);
    cleanupImageKeys([...uploadedImageKeys.current]);
    setSelectedTemplateId(template.id);
    setSelectedCopyPresetId(getJianyingDefaultCopyPresetId(template));
    setTemplatePickerExpanded(false);
    setPreferredDeviceId('');
    setSlotValues(createInitialJianyingSlotValues(template, clientName, orderImages));
    setFieldErrors({});
    setAudioFieldErrors({});
    setImageFieldErrors({});
    setVideoFieldErrors({});
    fieldRefs.current.clear();
    imageFieldRefs.current.clear();
    setActiveMaterialType('image');
    setMessage('');
  };

  const chooseCopyPreset = (presetId) => {
    if (!selectedTemplate) return;
    setSelectedCopyPresetId(presetId);
    setSlotValues((current) => applyJianyingCopyPreset(
      selectedTemplate,
      current,
      presetId,
      clientName,
    ));
    setFieldErrors({});
  };

  const setSlotValue = (slotKey, value) => {
    setSlotValues((current) => ({ ...current, [slotKey]: value }));
  };

  const setTextSlotValue = (slotKey, text) => {
    setSlotValue(slotKey, { kind: 'text', text });
    setFieldErrors((current) => {
      if (!current[slotKey]) return current;
      const nextErrors = { ...current };
      delete nextErrors[slotKey];
      return nextErrors;
    });
  };

  const setAudioSlotValue = (slotKey, value) => {
    const previousObjectKey = slotValues[slotKey]?.object_key;
    if (previousObjectKey && previousObjectKey !== value?.object_key) {
      cleanupAudioKeys([previousObjectKey]);
    }
    setSlotValue(slotKey, value);
    setAudioFieldErrors((current) => {
      if (!current[slotKey]) return current;
      const nextErrors = { ...current };
      delete nextErrors[slotKey];
      return nextErrors;
    });
  };

  const setVideoSlotValue = (slotKey, value) => {
    const previousObjectKey = slotValues[slotKey]?.object_key;
    if (previousObjectKey && previousObjectKey !== value?.object_key) {
      cleanupVideoKeys([previousObjectKey]);
    }
    setSlotValue(slotKey, value);
    setVideoFieldErrors((current) => {
      if (!current[slotKey]) return current;
      const nextErrors = { ...current };
      delete nextErrors[slotKey];
      return nextErrors;
    });
  };

  const clearImageFieldError = (fieldKey) => {
    setImageFieldErrors((current) => {
      if (!current[fieldKey]) return current;
      const nextErrors = { ...current };
      delete nextErrors[fieldKey];
      return nextErrors;
    });
  };

  const setImageSlotValue = (slotKey, value) => {
    const previousObjectKey = slotValues[slotKey]?.object_key;
    if (previousObjectKey && previousObjectKey !== value?.object_key) {
      cleanupImageKeys([previousObjectKey]);
    }
    setSlotValue(slotKey, value);
    clearImageFieldError(slotKey);
  };

  const registerFieldRef = useCallback((slotKey, node) => {
    if (node) fieldRefs.current.set(slotKey, node);
    else fieldRefs.current.delete(slotKey);
  }, []);

  const registerImageFieldRef = useCallback((fieldKey, node) => {
    if (node) imageFieldRefs.current.set(fieldKey, node);
    else imageFieldRefs.current.delete(fieldKey);
  }, []);

  const applyFieldErrors = useCallback((rawFieldErrors, summaryMessage) => {
    const normalizedErrors = normalizeJianyingFieldErrors(rawFieldErrors);
    const relatedErrors = Object.fromEntries(
      textSlots.flatMap((slot) => (
        normalizedErrors[slot.key] ? [[slot.key, normalizedErrors[slot.key]]] : []
      )),
    );
    const firstErrorKey = getFirstJianyingFieldErrorKey(selectedTemplate, relatedErrors);

    setFieldErrors(relatedErrors);
    setMessage(summaryMessage);
    if (!firstErrorKey) return false;

    setActiveMaterialType('text');
    requestAnimationFrame(() => {
      const field = fieldRefs.current.get(firstErrorKey);
      field?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      field?.focus();
    });
    return true;
  }, [selectedTemplate, textSlots]);

  const applyImageFieldErrors = useCallback((
    nextImageFieldErrors,
    summaryMessage,
  ) => {
    const firstErrorKey = getFirstJianyingImageFieldErrorKey(
      selectedTemplate,
      nextImageFieldErrors,
    );
    setImageFieldErrors(nextImageFieldErrors);
    setActiveMaterialType('image');
    setMessage(summaryMessage);
    if (!firstErrorKey) return false;

    requestAnimationFrame(() => {
      const field = imageFieldRefs.current.get(firstErrorKey);
      field?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      field?.focus();
    });
    return true;
  }, [selectedTemplate]);


  return {
    activeMaterialType,
    applyFieldErrors,
    applyImageFieldErrors,
    audioFieldErrors,
    audioSections,
    audioSlots,
    automation,
    canUseJianying,
    chooseCopyPreset,
    chooseOrder,
    chooseTemplate,
    cleanupImageKeys,
    clientName,
    devicePickerExpanded,
    devicesLoading,
    devicesMessage,
    eligibleOrders,
    fieldErrors,
    imageFieldErrors,
    imageSections,
    imageSlots,
    loading,
    materialTypes,
    message,
    openDevicePicker,
    orderFilter,
    orderImages,
    orderPickerExpanded,
    orders,
    ordersLoading,
    ordersMessage,
    preferredDeviceId,
    preferredDevices,
    processAllSmartCrop,
    processSingleSmartCrop,
    registerFieldRef,
    registerImageFieldRef,
    selectedCopyPresetId,
    selectedTemplate,
    selectedTemplateId,
    setActiveMaterialType,
    setAudioFieldErrors,
    setAudioSlotValue,
    setDevicePickerExpanded,
    setFieldErrors,
    setImageFieldErrors,
    setImageSlotValue,
    setMessage,
    setOrderFilter,
    setOrderPickerExpanded,
    setPreferredDeviceId,
    setSlotValues,
    setTemplatePickerExpanded,
    setTextSlotValue,
    setVideoFieldErrors,
    setVideoSlotValue,
    slotValues,
    smartCropStatus,
    templateAvailability,
    templatePickerExpanded,
    templates,
    textSections,
    textSlots,
    uploadedAudioKeys,
    uploadedImageKeys,
    uploadedVideoKeys,
    videoFieldErrors,
    videoSections,
    videoSlots,
  };
};

export default useJianyingCreateWorkspace;
